import { useEffect, useRef } from "react";
import api from "../services/api";
import { socketManager } from "../context/Socket/SocketContext";

/**
 * Hook para monitorar atividade do usuário e atualizar lastActivity no backend
 *
 * Detecta eventos de mouse, teclado e toque para considerar o usuário ativo
 * Envia heartbeat para o backend a cada minuto de atividade
 */
const useActivityMonitor = () => {
  const lastActivityRef = useRef(Date.now());
  const heartbeatIntervalRef = useRef(null);

  useEffect(() => {
    // Verificar se há token de autenticação
    const token = localStorage.getItem("token");
    if (!token) {
      console.log('useActivityMonitor: Usuário não autenticado, não monitorando atividade');
      return;
    }

    const updateActivity = () => {
      lastActivityRef.current = Date.now();
    };

    let autoLogoutConfig = {
      enabled: false,
      timeoutMinutes: 30
    };

    // Buscar configuração de inatividade da empresa
    const loadInactivitySettings = async () => {
      try {
        const { data } = await api.get("/time-monitoring/settings");
        if (data) {
          autoLogoutConfig.enabled = Boolean(data.autoLogoutInactive);
          if (data.inactivityTimeoutMinutes && Number(data.inactivityTimeoutMinutes) > 0) {
            autoLogoutConfig.timeoutMinutes = Number(data.inactivityTimeoutMinutes);
          }
        }
      } catch (err) {
        // Silencioso se usuário não for admin ou se rota não estiver disponível
      }
    };

    loadInactivitySettings();

    const sendHeartbeat = async () => {
      // Verificar novamente se há token antes de enviar
      const currentToken = localStorage.getItem("token");
      if (!currentToken) {
        return;
      }

      const now = Date.now();
      const timeSinceLastActivity = now - lastActivityRef.current;

      // Se logout automático por inatividade estiver ATIVADO e o tempo for excedido
      if (autoLogoutConfig.enabled && autoLogoutConfig.timeoutMinutes > 0) {
        const maxInactiveMs = autoLogoutConfig.timeoutMinutes * 60 * 1000;
        if (timeSinceLastActivity >= maxInactiveMs) {
          console.warn(`[useActivityMonitor] Sessão encerrada após ${autoLogoutConfig.timeoutMinutes} minutos de inatividade.`);
          localStorage.removeItem("token");
          localStorage.removeItem("companyId");
          localStorage.removeItem("userId");
          window.location.href = "/login";
          return;
        }
      }

      // Só enviar heartbeat se houve atividade no último minuto
      if (timeSinceLastActivity < 60000) {
        try {
          // Atualizar lastActivity via API
          await api.put("/users/activity");

          // Emitir heartbeat via socket (se estiver conectado)
          const socket = socketManager.GetSocket();
          if (socket && socket.connected) {
            socket.emit("heartbeat");
          }
        } catch (error) {
          // Não logar erro se for 401 (não autenticado)
          if (error?.response?.status !== 401) {
            console.error("Erro ao enviar heartbeat:", error);
          }
        }
      }
    };

    // Eventos que indicam atividade do usuário
    const activityEvents = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    // Adicionar listeners para todos os eventos de atividade
    activityEvents.forEach(event => {
      document.addEventListener(event, updateActivity, true);
    });

    // Iniciar interval de heartbeat e verificação de inatividade (a cada 1 minuto)
    heartbeatIntervalRef.current = setInterval(sendHeartbeat, 60000);

    // Enviar heartbeat inicial após 5 segundos (dar tempo para autenticação)
    setTimeout(sendHeartbeat, 5000);

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, updateActivity, true);
      });

      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, []);
};

export default useActivityMonitor;
